import React, { useLayoutEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import {
  BaseResponsiveDropdownProps,
  Dropdown,
  EllipsisDropdownAction,
  useResponsiveDropdown,
} from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'
import {
  NotificationInfoFragment,
  useAcceptShareMembershipInvitationMutation,
  useNotificationMarkAsReadMutation,
  useSetNotificationSubscriptionMutation,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'

import { ReactComponent as ChatBubbleIcon } from '@sketch/icons/bell-strikethrough-24'
import { ReactComponent as CheckmarkCircleIcon } from '@sketch/icons/checkmark-circle-17'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-circle-17'
import { ReactComponent as FolderIcon } from '@sketch/icons/folder-open-17'
import { ReactComponent as MailEnvelopeIcon } from '@sketch/icons/mail-envelope-17'
import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as ShareIcon } from '@sketch/icons/circle-arrow-up-right-17'
import { ReactComponent as MarkReadIcon } from '@sketch/icons/circle-checkmark-17'
import { ReactComponent as MarkUnreadIcon } from '@sketch/icons/img-mail-envelope-dot-17'

import {
  ActionButton,
  ActionLabel,
  ActionsBar,
  ActionsDivider,
  ShortcutHint,
  StickyActions,
} from './InboxPreview.styles'
import { getFolderPath, getNotificationPath } from './getNotificationPath'
import { recordPinned, recordUnpinned } from '../../views/Inbox/pinOrder'
import { ACTION_SHORTCUTS } from './constants'
import { useInboxActionShortcut } from './useInboxActionShortcut'
import { useSetNotificationState } from './useSetNotificationState'

/** Collapse to icon-only this many pixels before the labels actually overflow. */
const COLLAPSE_THRESHOLD = 30

const SeeDocumentAction = ({ path }: { path: string }) => {
  // Open the document in a new tab so the inbox stays put — the diagonal-arrow
  // icon already signals an "opens elsewhere" action.
  const goToDocument = () => window.open(path, '_blank')
  useInboxActionShortcut(ACTION_SHORTCUTS.seeDocument, goToDocument)

  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={ShareIcon}
      iconPosition="left"
      onClick={goToDocument}
    >
      <ActionLabel>Go to Document</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.seeDocument.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

const ViewFolderAction = ({ path }: { path: string }) => {
  // An archived/trashed folder still lives inside the app, so navigate in place
  // rather than opening a new tab like "Go to Document" does.
  const history = useHistory()
  const goToFolder = () => history.push(path)
  useInboxActionShortcut(ACTION_SHORTCUTS.viewFolder, goToFolder)

  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={FolderIcon}
      iconPosition="left"
      onClick={goToFolder}
    >
      <ActionLabel>View folder</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.viewFolder.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

interface DownloadActionProps {
  identifier: string
  assetPath: string
}

/**
 * Primary action for the downloadable-asset preview. Rendered inside the
 * preview card body (not the top actions bar) — see `DownloadableAssetPreview`.
 */
export const DownloadAction = ({
  identifier,
  assetPath,
}: DownloadActionProps) => {
  const [markAsRead, { loading }] = useNotificationMarkAsReadMutation({
    variables: { identifier },
    onError: 'show-toast',
    redirectErrors: true,
  })

  const download = () => {
    if (loading) return
    window.open(assetPath, '_self')
    markAsRead()
  }
  useInboxActionShortcut(ACTION_SHORTCUTS.download, download)

  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={DownloadIcon}
      iconPosition="left"
      loading={loading}
      onClick={download}
    >
      <ActionLabel>Download</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.download.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

const UnfollowThreadAction = ({
  annotationIdentifier,
}: {
  annotationIdentifier: string | undefined
}) => {
  const { showToast } = useToast()
  const [setSubscription, { loading }] = useSetNotificationSubscriptionMutation(
    {
      onError: 'show-toast',
      // Key the toast off the thread-level annotation status (the level this
      // action operates on), not the share — unfollowing one thread can leave
      // the share ON/PARTIAL, which would wrongly read "Following". An
      // annotation's status is only ever ON or OFF.
      onCompleted: data =>
        showToast(
          data.setNotificationSubscription.annotation?.subscriptionStatus ===
            'ON'
            ? 'Following'
            : 'Unfollowed'
        ),
    }
  )

  const unfollow = () => {
    if (loading) return
    setSubscription({ variables: { state: 'OFF', annotationIdentifier } })
  }
  useInboxActionShortcut(ACTION_SHORTCUTS.unfollow, unfollow)

  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={ChatBubbleIcon}
      iconPosition="left"
      loading={loading}
      onClick={unfollow}
    >
      <ActionLabel>Unfollow thread</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.unfollow.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

/**
 * Primary action for the add-share-member preview. Rendered inside the preview
 * card body (not the top actions bar) — see `AddShareMemberPreview`.
 */
export const AcceptAction = ({
  shareIdentifier,
}: {
  shareIdentifier: string
}) => {
  const { showToast } = useToast()
  const [acceptInvitation, { loading }] =
    useAcceptShareMembershipInvitationMutation({
      onError: 'show-toast',
      onCompleted: () => showToast('You now have access to this document'),
    })

  const accept = () => {
    if (loading) return
    acceptInvitation({ variables: { shareIdentifier } })
  }
  useInboxActionShortcut(ACTION_SHORTCUTS.accept, accept)

  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={CheckmarkCircleIcon}
      iconPosition="left"
      loading={loading}
      onClick={accept}
    >
      <ActionLabel>Accept</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.accept.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

const PinAction = ({
  notification,
}: {
  notification: NotificationInfoFragment
}) => {
  const { showToast } = useToast()
  const { identifier, isPinned } = notification

  const [updateNotification, { loading }] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    // Base the result on the state the server returned, not the captured
    // `isPinned` — by the time this fires the cache has already updated and
    // this closure would read the post-toggle value. Record the pin order so
    // the inbox list can surface the most recently pinned first.
    onCompleted: data => {
      const nowPinned = data.updateNotification?.notification?.isPinned
      if (nowPinned) {
        recordPinned(identifier)
      } else {
        recordUnpinned(identifier)
      }
      showToast(nowPinned ? 'Pinned' : 'Unpinned')
    },
  })

  // Ignore the shortcut while a toggle is in flight, mirroring the button's
  // disabled-by-loading state below.
  const togglePin = () => {
    if (loading) return
    updateNotification({
      variables: { identifier, input: { pin: !isPinned } },
    })
  }
  useInboxActionShortcut(ACTION_SHORTCUTS.pin, togglePin)

  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={PinIcon}
      iconPosition="left"
      // Show a spinner and block re-clicks while the toggle is in flight —
      // the bar only reorders/labels once the server confirms the new state.
      loading={loading}
      onClick={togglePin}
    >
      <ActionLabel>{isPinned ? 'Unpin' : 'Pin'}</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.pin.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

// Workspace invites can't be safely accepted inline — the AcceptWorkspaceInviteView
// branches on partner role, SSO state, and account/email mismatch (e.g. partners
// must complete the subscription flow). Push the user there instead of running
// acceptWorkspaceInvite from the inbox.
//
// Rendered inside the preview card body (not the top actions bar) — see
// `WorkspaceInvitedPreview`.
export const OpenWorkspaceInviteAction = ({ path }: { path: string }) => {
  const history = useHistory()
  const openInvite = () => history.push(path)
  useInboxActionShortcut(ACTION_SHORTCUTS.openInvite, openInvite)

  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={MailEnvelopeIcon}
      iconPosition="left"
      onClick={openInvite}
    >
      <ActionLabel>Open invite</ActionLabel>
      <ShortcutHint>{ACTION_SHORTCUTS.openInvite.toUpperCase()}</ShortcutHint>
    </ActionButton>
  )
}

// Rendered inside the preview card body (not the top actions bar) — see
// `WorkspaceInvitedPreview`.
export const GoToWorkspaceAction = ({
  workspaceId,
}: {
  workspaceId: string
}) => {
  const history = useHistory()
  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={ShareIcon}
      iconPosition="left"
      onClick={() => history.push(routes.WORKSPACE.create({ workspaceId }))}
    >
      <ActionLabel>Go to workspace</ActionLabel>
    </ActionButton>
  )
}

interface ResolvedAction {
  id: string
  element: React.ReactElement
}

/**
 * Builds the list of action elements applicable to a notification. The
 * returned elements are not mounted yet — React only runs each component's
 * hooks when the element is actually rendered, so unused actions never pay the
 * cost of their mutation hook.
 */
const resolveActions = (
  notification: NotificationInfoFragment
): ResolvedAction[] => {
  const path = getNotificationPath(notification)

  // Pin/unpin applies to every notification type.
  const actions: ResolvedAction[] = [
    { id: 'pin', element: <PinAction notification={notification} /> },
  ]

  const pushSeeDocument = () => {
    if (path) {
      actions.push({
        id: 'see-document',
        element: <SeeDocumentAction path={path} />,
      })
    }
  }

  switch (notification.__typename) {
    case 'CommentNotification':
    case 'MentionNotification': {
      // An annotation's subscription status is only ever ON or OFF (PARTIAL
      // applies at the share level, never to a single thread).
      const isSubscribed =
        notification.comment?.annotation.subscriptionStatus === 'ON'

      if (isSubscribed) {
        actions.push({
          id: 'unfollow',
          element: (
            <UnfollowThreadAction
              annotationIdentifier={notification.comment?.annotation.identifier}
            />
          ),
        })
      }
      pushSeeDocument()
      break
    }
    // Archived, trashed and restored folders still exist, so offer a "View
    // folder" link to the archive / trash / project view. A permanently-deleted
    // folder is gone, so it only gets pin/unpin.
    case 'FolderArchivedNotification':
    case 'FolderTrashedNotification':
    case 'FolderRestoredNotification': {
      const folderPath = getFolderPath(notification)
      if (folderPath) {
        actions.push({
          id: 'view-folder',
          element: <ViewFolderAction path={folderPath} />,
        })
      }
      break
    }
    case 'FolderPermanentlyDeletedNotification':
      break
    // The workspace-invite primary action (Open invite / Go to workspace) lives
    // inside the preview card body, not this bar — see `WorkspaceInvitedPreview`
    // and `WorkspaceInviteAcceptedPreview`. There's no document to open either,
    // so they only get pin/unpin here.
    case 'WorkspaceInvitedNotification':
    case 'WorkspaceInviteAcceptedNotification':
      break
    // The add-share-member (Accept) and downloadable-asset (Download) primary
    // actions live inside the preview card body, not this bar — they only need
    // the shared "Go to Document" secondary action here.
    default:
      pushSeeDocument()
  }

  return actions
}

interface MoreActionsDropdownProps extends BaseResponsiveDropdownProps {
  notification: NotificationInfoFragment
}

const MoreActionsDropdown = ({
  notification,
  hide,
}: MoreActionsDropdownProps) => {
  const setNotificationState = useSetNotificationState()
  const isUnread = notification.state === 'UNREAD'

  return (
    <Dropdown.Item
      icon={isUnread ? <MarkReadIcon /> : <MarkUnreadIcon />}
      onClick={() => {
        setNotificationState(
          notification.identifier,
          notification.__typename,
          isUnread ? 'READ' : 'UNREAD'
        )
        hide()
      }}
    >
      {isUnread ? 'Mark as read' : 'Mark as unread'}
    </Dropdown.Item>
  )
}

const MoreActionsMenu = ({
  notification,
}: {
  notification: NotificationInfoFragment
}) => {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: MoreActionsDropdown,
    dropdownProps: { notification },
    placement: 'bottom-end',
    usePortal: true,
  })

  return (
    <>
      <EllipsisDropdownAction label="More actions" {...buttonProps} />
      {content}
    </>
  )
}

interface InboxPreviewActionsProps {
  notification: NotificationInfoFragment
  scrolled?: boolean
}

export const InboxPreviewActions = ({
  notification,
  scrolled,
}: InboxPreviewActionsProps) => {
  const actions = resolveActions(notification)
  const barRef = useRef<HTMLDivElement>(null)

  // Re-measure only when the rendered action set changes, not on every render
  // (the actions array is a new reference each render).
  const actionsKey = actions.map(action => action.id).join('|')

  // Collapse the bar to icons-only when the labelled buttons don't fit on a
  // single row. We measure the bar's content (`scrollWidth`) against the
  // available width of its parent — which is reliably constrained, whereas the
  // bar is capped at that width and clips its own content. The data attribute
  // is toggled imperatively (not via state) so the measurement always runs
  // against the labelled width, avoiding a measure → re-render → re-measure
  // loop.
  useLayoutEffect(() => {
    const bar = barRef.current
    const container = bar?.parentElement
    if (!bar || !container || typeof ResizeObserver === 'undefined') return

    const evaluate = () => {
      bar.removeAttribute('data-icon-only')
      if (bar.scrollWidth > container.clientWidth - COLLAPSE_THRESHOLD) {
        bar.setAttribute('data-icon-only', 'true')
      }
    }

    evaluate()
    const observer = new ResizeObserver(evaluate)
    observer.observe(container)
    return () => observer.disconnect()
  }, [actionsKey])

  return (
    <StickyActions $scrolled={scrolled}>
      <ActionsBar ref={barRef}>
        {actions.map(action => (
          <React.Fragment key={action.id}>{action.element}</React.Fragment>
        ))}
        {actions.length > 0 && <ActionsDivider aria-hidden />}
        <MoreActionsMenu notification={notification} />
      </ActionsBar>
    </StickyActions>
  )
}
